import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Download, Paperclip, Printer, Search } from '../../../icons';
import {
  approveDocument,
  deleteDraft as deleteDraftApi,
  extractErrorMessage,
  getDocument,
  recallDocument,
  rejectDocument,
  submitDraft as submitDraftApi,
} from '../../../api/approval';
import { listDocTypes, type DocTypeDto } from '../../../api/approval-doc-type';
import { requestPresignedDownload } from '../../../api/files';
import type { ApprovalAttachmentDto, ApprovalDocumentDetail } from '../../../types/approval';
import { Avatar } from '../../../components/shared/Avatar/Avatar';
import { formatBytes } from '../../../lib/attachments';
import { tApproval } from '../../../lib/approval-i18n';
import { buildDraftSubmitBody } from '../../../lib/approval-submit-body';
import { sanitizeMailHtml } from '../../../lib/sanitize';
import { useAuthStore } from '../../../store/authStore';
import { ApprovalStampRow } from '../ApprovalStampRow';
import { ApprovalBasisPanel, ApprovalFormView } from '../ApprovalFormView';
import { ApprovalHistory } from '../ApprovalHistory';
import { NOT_READY_TEXT } from '../box/approval-box-types';
import {
  DOC_TABS, flowNodes, lineProgress, mdhm, referrerNodes, ymdhm,
  type DocTab,
} from './approval-doc-types';
import '../box/approval-box.css';
import './approval-doc.css';
// 결재 상태 표 한 벌(2026-10-18 · web 레인 W11b · 결정 D10) — 강조는 «내 차례» 한 곳, 나머지는 회색 글
import '../box/approval-box-status.css';
import { approvalStatusMark } from '../../../lib/approval-status';

/**
 * 결재 문서 상세 (2026-09-14 · 과제 L3 · `/approval/doc/:id`).
 *
 * 시안 `axi-approval-detail` v2(사용자 확인 — 그대로 반영):
 *   [머리 카드: 기안자 · 제목 · 상태 · 문서번호 · 결재선 n/N] → [탭: 본문 · 결재선 · 첨부 · 의견 · 이력] → [결재 막대].
 *
 * ★★ 기능은 옛 `ApprovalDetail` 을 **하나도 빼지 않고** 새 자리로 옮겼다 —
 *    승인 · 반려(의견) · 회수 · 임시보관 올리기 · 지우기 · 인쇄 · 결재란 도장 · 서식 칸 · 본문(sanitize) ·
 *    이 문서가 쓴 기준 · 예산 안내 · 첨부 내려받기 · 이력(누를 때 읽음) · 참고자 명단.
 * ★ 옛 `ApprovalDetail` 은 그대로다 — 문서함 두 열 화면(`/approval/legacy/*`)이 쓴다.
 * ★ 서버에 없는 것(보류 · 재알림 · PDF · 의견 스레드 · 보존 기간 · 다시 기안)은 잠긴 단추 · 자리표시 — `FUTURE_DOC_REQUESTS`.
 */
interface Props {
  documentId: string;
  currentUserId: string;
}

type Action = 'approve' | 'reject' | 'recall' | 'submit' | 'delete';

export function ApprovalDocView({ documentId, currentUserId }: Props): JSX.Element {
  const navigate = useNavigate();
  // 내 이름 — 결재란 내 칸에서 이름 도장을 즉석으로 만든다(ApprovalStampRow).
  const myName = useAuthStore((s) => s.user?.name ?? '');
  const [doc, setDoc] = useState<ApprovalDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  /** 상신은 끝났고 예산만 안 붙은 경우 — 잘못이 아니라 알림이다(ApprovalDetail 과 같은 규칙). */
  const [notice, setNotice] = useState<string | null>(null);
  /** 단추를 눌러 실패한 것 — 문서는 남기고 막대 곁에 한 줄(2026-09-13 · ⑦). */
  const [actionError, setActionError] = useState<string | null>(null);
  const [tab, setTab] = useState<DocTab>('body');
  const [comment, setComment] = useState('');
  const [types, setTypes] = useState<DocTypeDto[]>([]);
  const [find, setFind] = useState('');

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDoc(await getDocument(documentId));
    } catch (err) {
      setError(extractErrorMessage(err));
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { void refetch(); }, [refetch]);

  useEffect(() => {
    let alive = true;
    void listDocTypes().then((t) => { if (alive) setTypes(t); }).catch(() => { if (alive) setTypes([]); });
    return () => { alive = false; };
  }, []);

  const typeName = useMemo(() => {
    const m = new Map(types.map((t) => [t.code, t.name]));
    return (code: string) => m.get(code) ?? code;
  }, [types]);

  const handleAction = useCallback(async (action: Action) => {
    if (!doc) return;
    setActing(true);
    setActionError(null);
    try {
      if (action === 'approve') await approveDocument(doc.id, comment || undefined);
      else if (action === 'reject') await rejectDocument(doc.id, comment || undefined);
      else if (action === 'submit') {
        /*
          ★★★ 결재자로는 APPROVE 줄만 — 규칙은 `lib/approval-submit-body.ts` 한 곳이다.
          ⛔ 그 거르개는 «결재 안전장치»다: 참고자(REFER · 운영 13줄)가 섞이면 최종결재자가 바뀐다.
          ⚠ 2026-09-27 — 「agreerIds 는 바뀐 게 없으면 안 싣는다」는 종전 규칙은 **없어졌다**(그 칸이 없다).
        */
        const body = buildDraftSubmitBody(doc.lines);
        if (body.approverIds.length === 0) {
          throw new Error('결재자가 없습니다 — 기안 화면에서 결재선을 골라 다시 만들어 주세요.');
        }
        const res = await submitDraftApi(doc.id, body);
        // ★★ 상신에서도 예산 이야기를 한다(예산 판 B3) — 못 붙었다는 사실이지 실패가 아니다.
        const budgetNotes = res.expenseRequest?.notes ?? [];
        if (budgetNotes.length > 0) setNotice(budgetNotes.join('\n'));
      } else if (action === 'delete') {
        if (!window.confirm('이 임시보관을 지웁니다. 되돌릴 수 없습니다. 계속할까요?')) return;
        await deleteDraftApi(doc.id);
        // 지운 문서에 머물 이유가 없다 — 임시 함으로 돌아간다.
        navigate('/approval/box?box=temp', { replace: true });
        return;
      } else await recallDocument(doc.id);
      setComment('');
      await refetch();
    } catch (err) {
      // ★ 문서는 남긴다 — 실패는 막대 곁에만. DRAFT 는 서버에서도 그대로다.
      setActionError(extractErrorMessage(err));
    } finally {
      setActing(false);
    }
  }, [doc, comment, refetch, navigate]);

  const onFind = (e: FormEvent) => {
    e.preventDefault();
    const v = find.trim().slice(0, 100);
    navigate(v ? `/approval/box?box=all&q=${encodeURIComponent(v)}` : '/approval/box?box=all');
  };

  const top = (
    <header className="apb-top">
      <div>
        <nav className="apb-crumb" aria-label="위치"><span>전자결재</span><i>/</i><Link to="/approval/box">결재함</Link><i>/</i><span>문서</span></nav>
        <h1 className="apb-title">결재 문서</h1>
      </div>
      {/* 시안의 머리 찾기 — 결재함 「전체 문서」 로 넘겨 서버 검색(q)에 맡긴다 */}
      <form className="apb-search" role="search" onSubmit={onFind}>
        <label>
          <span className="apb-sr">결재함에서 문서 찾기</span>
          <Search aria-hidden />
          <input type="search" value={find} maxLength={100} placeholder="문서번호, 제목, 기안자로 찾기" onChange={(e) => setFind(e.target.value)} />
        </label>
      </form>
      <div className="apd-topacts">
        {/* ★ 「뒤로」 는 브라우저 뒤로다 — 어디서 왔든(홈 · 결재함 · 링크) 그 자리로 돌아간다(종전 그대로). */}
        <button type="button" className="apb-btn apb-btn--line" onClick={() => navigate(-1)}><ArrowLeft aria-hidden />뒤로</button>
        <Link className="apb-btn apb-btn--line" to="/approval/box">목록으로</Link>
        <Link className="apb-btn" to="/approval/new">+ 새 기안</Link>
      </div>
    </header>
  );

  if (!doc) {
    return (
      <div className="apb-root apd">
        {top}
        {loading
          ? <p className="apd-note">불러오는 중…</p>
          : <p className="apb-alert" role="alert"><AlertCircle aria-hidden />{error ?? '문서를 찾을 수 없습니다.'}</p>}
      </div>
    );
  }

  const isDrafter = doc.drafterId === currentUserId;
  // myActionable 은 서버가 위임까지 따져 준 값 — 그대로 믿는다(F-3).
  const canApprove = doc.myActionable;
  const isDraft = isDrafter && doc.status === 'DRAFT';
  const canRecall = isDrafter && doc.status === 'IN_PROGRESS';
  const canComment = canApprove;
  const rejected = doc.status === 'REJECTED';

  const mark = approvalStatusMark(doc.status, Boolean(doc.myActionable));
  const nodes = flowNodes(doc);
  const refs = referrerNodes(doc);
  const { done, total } = lineProgress(doc);
  const attachments = doc.attachments ?? [];
  const comments = nodes.filter((n) => n.comment);
  const drafterName = doc.basis?.drafter.name ?? '기안자';
  const drafterPlace = [doc.basis?.drafter.team ?? doc.basis?.drafter.division, doc.basis?.drafter.position].filter(Boolean).join(' ');
  // ★ 문서번호 — 임시보관 · 옛 문서는 없는 것이 정상이라 자리를 안 그린다(판 P4).
  const meta = [
    doc.docNo,
    // ★ 리뷰 #1272 — 임시보관(DRAFT)은 `draftedAt` 이 남아 있어도(회수 뒤 다시 임시 등) 「상신」 이라 적지 않는다. 상태가 기준이다.
    doc.status === 'DRAFT' ? '임시보관 · 아직 상신 전' : doc.draftedAt ? `${ymdhm(doc.draftedAt)} 상신` : null,
    `보존 기간 ${NOT_READY_TEXT}`,
  ].filter(Boolean).join(' · ');
  const tabCount: Partial<Record<DocTab, number>> = { files: attachments.length, talk: comments.length };

  const barText = canApprove ? '내 차례 — 승인 · 의견은 결재선에 함께 남습니다'
    : isDraft ? '아직 아무에게도 가지 않았습니다 — 올리면 그때 결재자에게 알림이 가고, 근태·보고 신청도 그때 만들어집니다'
    : doc.status === 'DRAFT' ? '임시보관 문서입니다 — 아직 아무에게도 가지 않았습니다'
    : doc.status === 'IN_PROGRESS' ? `진행 중 — ${doc.waiting?.name ? `${doc.waiting.name} 대기` : '다음 결재 대기'}${canRecall ? ' · 기안자는 다음 결재 전까지 회수할 수 있습니다' : ''}`
    : doc.status === 'APPROVED' ? `결재가 끝난 문서입니다${doc.completedAt ? ` — ${ymdhm(doc.completedAt)} 최종 승인` : ''}`
    : rejected ? '반려되어 기안자에게 돌려보냈습니다 — 반려 사유는 결재선에 있습니다'
    : '기안자가 회수한 문서입니다';

  // ★★★ 결재자가 예산을 함께 본다(예산 판 B4) — 막지 않고 판단 재료만 얹는다.
  const budget = doc.budget;
  const budgetBlock = budget && (budget.over.length > 0 || budget.unlinked > 0) ? (
    <div className="apd-notice apd-notice--warn" role="status">
      {budget.over.map((o) => (
        <span key={o.budgetName}>«{o.budgetName}» 예산을 <b>{o.amount.toLocaleString()}원</b> 넘습니다.</span>
      ))}
      {budget.unlinked > 0 && <span>내역 {budget.unlinked}줄이 <b>예산에 붙지 않았습니다.</b></span>}
      <small>예산은 결재를 막지 않습니다 — 판단에 참고하시라고 적습니다.</small>
    </div>
  ) : null;

  return (
    <div className="apb-root apd">
      {top}

      <section className="apb-card apd-card" aria-label="문서 상세">
        <div className="apd-head">
          <Avatar userId={Number(doc.drafterId) || 0} name={drafterName} size="lg" />
          <div className="apd-hmain">
            <div className="apd-hname">
              <h2>{doc.title}</h2>
              {mark.emphasis === 'mine'
                ? <span className="apb-badge apb-badge--mine">{mark.label}</span>
                : <span className="apb-stat">{mark.label}</span>}
              {doc.isUrgent && <span className="apb-badge apb-badge--bad">{tApproval('approval.urgent_badge')}</span>}
            </div>
            <p>{drafterName}{drafterPlace ? ` · ${drafterPlace}` : ''} 기안 · {typeName(doc.type)}</p>
            <p className="apd-meta num">{meta}</p>
          </div>
          <div className="apd-fill" role="img" aria-label={`결재선 ${done}/${total}`}>
            <span>결재선</span><b className="num">{done} / {total}</b>
            <span className="apd-bar"><i className={rejected ? 'is-bad' : undefined} style={{ width: `${Math.round((done / total) * 100)}%` }} /></span>
          </div>
        </div>

        <div className="apd-tabbar">
          <div className="apd-tabs" role="tablist" aria-label="문서 보기">
            {DOC_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                id={`apd-t-${t.key}`}
                aria-selected={tab === t.key}
                aria-controls={`apd-p-${t.key}`}
                className={tab === t.key ? 'is-on' : undefined}
                onClick={() => setTab(t.key)}
              >
                {t.label}{tabCount[t.key] !== undefined && <em className="num">{tabCount[t.key]}</em>}
              </button>
            ))}
          </div>
          <div className="apd-tabend">
            {/* ★★ 인쇄는 모든 문서에서 — 종이로 남기는 자리(판 8 잔여). @media print 가 카드만 남긴다. */}
            <button type="button" className="apb-btn apb-btn--line apb-btn--sm" title="이 결재 문서를 인쇄합니다" onClick={() => window.print()}><Printer aria-hidden />인쇄</button>
            <button type="button" className="apb-btn apb-btn--line apb-btn--sm" disabled title={`문서 PDF — ${NOT_READY_TEXT}`}><Download aria-hidden />문서 PDF</button>
          </div>
        </div>

        <div className="apd-pane" role="tabpanel" id={`apd-p-${tab}`} aria-labelledby={`apd-t-${tab}`}>
          {tab === 'body' && (
            <div className="apd-doc">
              <span className="apd-form">{typeName(doc.type)} 양식</span>
              <h3 className="apd-doctitle">{doc.title}</h3>
              {/* 결재란 — 종이 문서의 도장 줄. 내 칸에서 서명을 즉석으로 만든다. */}
              <ApprovalStampRow doc={doc} myUserId={currentUserId} myName={myName} onSignatureChanged={() => { void refetch(); }} />
              {/* 서식에 채운 값 — 본문 위(판 9) */}
              <ApprovalFormView fields={doc.form?.fields ?? []} data={doc.formData} />
              <section className="apd-content" aria-label={tApproval('approval.detail.body_aria')}>
                {doc.contentType === 'HTML' ? (
                  // Cycle 62 — HTML 본문은 렌더 직전 sanitize(XSS 방어 · 메일과 같은 정책).
                  // eslint-disable-next-line react/no-danger -- sanitizeMailHtml 통과 후 안전.
                  <div className="approval-detail__content" dangerouslySetInnerHTML={{ __html: sanitizeMailHtml(doc.content) }} />
                ) : (
                  <pre className="approval-detail__content approval-detail__content--text">{doc.content}</pre>
                )}
              </section>
              {/* 이 문서가 쓴 기준 — 본문 아래 접어서(판 9) */}
              <ApprovalBasisPanel basis={doc.basis} />
            </div>
          )}

          {tab === 'line' && (
            <>
              <ol className="apd-flow" aria-label="결재선">
                {nodes.map((n) => (
                  <li key={n.key} className={`is-${n.tone}`}>
                    <span className="fm" aria-hidden />
                    <div className="lw">
                      <Avatar userId={n.userId} name={n.name} src={n.avatarUrl} size="sm" />
                      <div>
                        <div className="ln">{n.slot} · <b>{n.name}</b>{n.sub && <em> {n.sub}</em>}</div>
                        <div className="lm num">{n.state}</div>
                        {n.comment && <div className="lq">“{n.comment}”</div>}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
              <h3 className="apd-ptitle">참고자</h3>
              {refs.length === 0 ? <p className="lm">참고자가 없습니다</p> : (
                <ul className="apd-refs" aria-label="참고자">
                  {refs.map((r) => (
                    <li key={r.key} className="ref"><Avatar userId={r.userId} name={r.name} src={r.avatarUrl} size="xs" /><b>{r.name}</b></li>
                  ))}
                </ul>
              )}
              <p className="apd-fnote">참고자는 결재하지 않고 문서를 볼 수만 있습니다</p>
            </>
          )}

          {tab === 'files' && (
            <>
              <div className="apb-tw">
                <table className="apd-table">
                  <thead><tr><th>파일</th><th className="r">크기</th><th className="r">받기</th></tr></thead>
                  <tbody>
                    {attachments.length === 0
                      ? <tr className="none"><td colSpan={3}>첨부 파일이 없습니다</td></tr>
                      : attachments.map((a) => <FileRow key={a.fileId} file={a} />)}
                  </tbody>
                </table>
              </div>
              <p className="apd-fnote">첨부는 문서와 함께 보관됩니다 · 보존 기간 {NOT_READY_TEXT}</p>
            </>
          )}

          {tab === 'talk' && (
            <>
              <div className="apd-cmts">
                {comments.length === 0 ? <div className="apd-cmt-empty">아직 의견이 없습니다</div> : comments.map((n) => (
                  <div key={n.key} className="apd-cmt">
                    <Avatar userId={n.userId} name={n.name} src={n.avatarUrl} size="sm" />
                    <div>
                      <div className="ln"><b>{n.name}</b> <em className="num">{mdhm(n.at)}</em> <em>{n.slot} · {n.state.split(' · ')[0]}</em></div>
                      <div className="ct2">{n.comment}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="apd-addbox">
                <h4>의견 남기기</h4>
                <textarea
                  className="apd-ta"
                  aria-label="의견"
                  rows={3}
                  maxLength={2000}
                  value={comment}
                  disabled={!canComment}
                  placeholder={canComment ? '승인 · 반려와 함께 결재선에 남습니다' : '결재 차례일 때 남길 수 있습니다'}
                  onChange={(e) => setComment(e.target.value)}
                />
                <div className="apd-addrow">
                  <label className="apd-toggle" title={`기안자에게만 보이기 — ${NOT_READY_TEXT}`}>
                    <input type="checkbox" disabled /><i aria-hidden />기안자에게만 보이기
                  </label>
                  <button type="button" className="apb-btn apb-btn--sm" disabled title={`의견만 따로 남기기 — ${NOT_READY_TEXT}`}>의견 등록</button>
                </div>
              </div>
            </>
          )}

          {/* 이력 — 누를 때만 읽는다(판 P6 · 감사 로그가 정본) */}
          {tab === 'log' && <ApprovalHistory documentId={doc.id} />}
        </div>

        {(notice || budgetBlock || actionError) && (
          <div className="apd-msgs">
            {notice && (
              <div className="apd-notice" role="status">
                <b>예산에는 붙지 않았습니다.</b>
                {notice.split('\n').map((n) => <span key={n}>{n}</span>)}
                <small>지출 기록은 남습니다 — 나중에 예산이 생기면 담당자가 이어 붙일 수 있습니다.</small>
              </div>
            )}
            {budgetBlock}
            {actionError && <p className="apb-alert" role="alert"><AlertCircle aria-hidden />{actionError}</p>}
          </div>
        )}

        <div className="apd-savebar" role="group" aria-label={tApproval('approval.detail.actions_aria')}>
          <span className="apd-sbtext">{barText}{canComment && comment.trim() ? ` · 의견 ${comment.trim().length}자 함께 남김` : ''}</span>
          {canApprove && (
            <>
              <button type="button" className="apb-btn apb-btn--danger apb-btn--sm" disabled={acting} onClick={() => void handleAction('reject')}>{tApproval('approval.detail.btn_reject')}</button>
              <button type="button" className="apb-btn apb-btn--line apb-btn--sm" disabled title={`보류 — ${NOT_READY_TEXT}`}>보류</button>
              <button type="button" className="apb-btn apb-btn--sm" disabled={acting} onClick={() => void handleAction('approve')}>{tApproval('approval.detail.btn_approve')}</button>
            </>
          )}
          {/*
            ⛔ 합의 전용 단추 쌍(「동의」·「반대」)을 지웠다 (2026-09-27 · 합의 기능 삭제).
              같은 막대에 두 쌍이 서던 위험 — 「반려는 문서를 멈추고 반대는 안 멈추는데 나란히 선다」 —
              도 그 단추가 사라져 함께 없어졌다.
          */}
          {isDraft && (
            <>
              <button type="button" className="apb-btn apb-btn--line apb-btn--sm" disabled={acting} onClick={() => void handleAction('delete')}>지우기</button>
              <button type="button" className="apb-btn apb-btn--sm" disabled={acting} onClick={() => void handleAction('submit')}>올리기</button>
            </>
          )}
          {canRecall && (
            <>
              <button type="button" className="apb-btn apb-btn--line apb-btn--sm" disabled={acting} onClick={() => void handleAction('recall')}>{tApproval('approval.detail.btn_recall')}</button>
              <button type="button" className="apb-btn apb-btn--line apb-btn--sm" disabled title={`재알림 보내기 — ${NOT_READY_TEXT}`}>재알림 보내기</button>
            </>
          )}
          {doc.status === 'APPROVED' && (
            <button type="button" className="apb-btn apb-btn--sm" disabled title={`PDF 받기 — ${NOT_READY_TEXT}`}>PDF 받기</button>
          )}
          {rejected && (
            <>
              <button type="button" className="apb-btn apb-btn--line apb-btn--sm" onClick={() => setTab('line')}>반려 사유 보기</button>
              {isDrafter && <button type="button" className="apb-btn apb-btn--line apb-btn--sm" disabled title={`다시 기안 — ${NOT_READY_TEXT}`}>다시 기안</button>}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/** 첨부 한 줄 — 내려받기는 파일 권한 사다리 8번 칸(기안자 · 결재선 · 공개)이 연다. 채팅 · 메일과 같은 presigned 경로. */
function FileRow({ file }: { file: ApprovalAttachmentDto }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await requestPresignedDownload(file.fileId);
      const a = document.createElement('a');
      a.href = res.downloadUrl;
      a.download = res.name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '내려받지 못했습니다');
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        <span className="apd-file"><Paperclip aria-hidden />{file.name}</span>
        {file.fromWorkspace && <small className="apd-sub"> · 워크스페이스에서 고른 파일</small>}
        {err && <small className="apd-err"> · {err}</small>}
      </td>
      <td className="r num">{formatBytes(file.sizeBytes)}</td>
      <td className="r">
        <button type="button" className="apb-btn apb-btn--line apb-btn--sm" disabled={busy} aria-label={`${file.name} 받기`} onClick={() => void download()}>
          <Download aria-hidden />받기
        </button>
      </td>
    </tr>
  );
}

export default ApprovalDocView;
